import { useState } from "react";

import { assetUrl } from "./api.js";
import { markTint, monogram } from "./org-mark.js";

/**
 * An organization's mark: its logo when there is one that loads, its monogram otherwise.
 *
 * ## Why this is a component and not four `logoUrl ? … : …` branches
 *
 * It was four branches, and every one of them asked the wrong question. `logoUrl ? <img> : …` is a
 * **null check**, and a null `logoUrl` is not the only way a donor ends up with no logo — the URL
 * can 404, the host can be down, the file can be deleted, a CDN can blip, a network can block the
 * origin. In every one of those the branch chose `<img>` and the browser drew its own broken-image
 * glyph: a torn page, in the tile where the organization's identity belongs.
 *
 * Found on the **checkout**, where it matters most. The mark is on that screen for one reason —
 * the single irreversible mistake available there is giving to the wrong organization, and a
 * community recognises its own mark faster than it reads its own legal name. A torn page is worse
 * than no mark at all: it is the page telling the donor that something about this organization is
 * broken, at the moment they are deciding whether to trust it with money.
 *
 * iOS never had this. `OrgMark.swift` routes `AsyncImage`'s failure and loading phases to the same
 * monogram, with the reasoning written down: "a spinner that resolves to a monogram is two states
 * for one answer". This is that behaviour, restated for the web, so the two surfaces answer a dead
 * URL the same way.
 *
 * ## The monogram is the ordinary path
 *
 * Most directory records are sourced rather than claimed, so most have no logo at all. That is why
 * the fallback is a designed monogram and not a placeholder frame — and why `onError` swapping to
 * it is not a degraded state a donor can perceive as one.
 */
export function OrgMark({
  displayName,
  logoUrl,
  className,
  size,
  loading,
}: {
  readonly displayName: string;
  readonly logoUrl: string | null;
  /** The block this mark belongs to — `org__mark`, `landing__mark`, `result__mark`. */
  readonly className: string;
  readonly size: number;
  /** `lazy` for list rows, which may hold more marks than a screenful. */
  readonly loading?: "lazy" | "eager";
}) {
  /*
    One flag, and it is per-URL rather than a bare boolean: a component that has already failed
    once must be able to try again when it is handed a *different* organization, which happens on
    every re-render of a list row. Keying the failure to the URL that produced it is what makes
    that automatic — a new `logoUrl` is simply not the failed one.
  */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = logoUrl !== null && logoUrl !== failedUrl;

  if (!showImage) {
    return (
      <span
        className={`${className} ${className}--placeholder mark-tint`}
        /* One of six measured ground/ink pairs, chosen from the name. Fifty organizations in one
           list used to draw fifty identical tiles, which is a mark that identifies nothing. */
        data-tint={markTint(displayName)}
        aria-hidden="true"
      >
        {monogram(displayName)}
      </span>
    );
  }

  return (
    <img
      className={className}
      src={assetUrl(logoUrl)}
      /* Decorative: the name is rendered as text beside or beneath every one of these, so an alt
         would make a screen reader say it twice. */
      alt=""
      width={size}
      height={size}
      /* No referrer: a logo is a decoration and must not tell its host which donation page the
         donor is on — which, for this cohort, is an inference about religion. */
      referrerPolicy="no-referrer"
      {...(loading ? { loading } : {})}
      onError={() => setFailedUrl(logoUrl)}
    />
  );
}
